import {
  Avatar,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import pageStyle from "./CustomerDetails.module.css";
import {
  AccessTime,
  Assignment,
  Call,
  Delete,
  Email,
  Map,
  MailOutline,
  Photo as MUIPhoto,
  Sms,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { MuiTelInput } from "mui-tel-input";
import { useState, useEffect, ReactNode } from "react";
import { formatMoney, states, statusToColor } from "../utils";
import {
  APICustomer,
  deleteCustomer,
  emptyCustomer,
  getCustomer,
  getCustomerActivities,
  saveCustomer,
} from "../api/customers";
import { APIActivity, ActivityPhoto } from "../api/activities";
import { APIProposalStatus } from "../api/proposals";
import { APIEmail, getAllEmails } from "../api/emails";
import SmsThread from "../components/SmsThread";

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={pageStyle.section}>
      <div className={pageStyle.sectionHeader}>
        <Typography variant="subtitle1">{title}</Typography>
        {action}
      </div>
      {children}
    </section>
  );
}

function CustomerDetailsPage({ id }: { id?: string }) {
  const navigate = useNavigate();

  useEffect(() => {
    getCustomer(id).then((v) => setCustomer(v));
    if (id) {
      getCustomerActivities(id).then((v) => setCustomerActivity(v));
    } else {
      setCustomerActivity([]);
    }
  }, [id]);

  const [customerActivity, setCustomerActivity] = useState<APIActivity[]>([]);
  const [emails, setEmails] = useState<APIEmail[]>([]);

  // Emails about this customer or about one of their proposals.
  useEffect(() => {
    if (!id) {
      setEmails([]);
      return;
    }
    getAllEmails().then((all) => {
      const proposalIds = new Set(
        customerActivity.filter((a) => a.type === "proposal").map((a) => a.id)
      );
      setEmails(
        all
          .filter(
            (e) =>
              (e.refType === "customer" && e.refId === id) ||
              (e.refType === "proposal" && proposalIds.has(e.refId || ""))
          )
          .sort((a, b) => (b.generatedAt || 0) - (a.generatedAt || 0))
      );
    });
  }, [id, customerActivity]);
  const [customer, setCustomer] = useState<APICustomer>(emptyCustomer());

  // Every change saves immediately, as before.
  const updateCustomer = (change: Partial<APICustomer>) => {
    const updated = { ...customer, ...change };
    setCustomer(updated);
    handleSave(updated);
  };

  const handleSave = async (cus: APICustomer) => {
    const c = await saveCustomer(cus);
    if (!cus.id) {
      navigate(`/customers?id=${c.id}`, { replace: true });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${name || "this customer"}?`)) {
      return;
    }
    if (id) {
      await deleteCustomer(id);
    }
    navigate("/customers");
  };

  const name = `${customer.firstName} ${customer.lastName}`.trim();
  const initials =
    (customer.firstName[0] || "").toUpperCase() +
    (customer.lastName[0] || "").toUpperCase();
  const address = customer.addressLine1
    ? [
        customer.addressLine1,
        customer.addressLine2,
        customer.city,
        `${customer.state} ${customer.zip}`.trim(),
      ]
        .filter(Boolean)
        .join(", ")
    : "";
  const phoneDigits = customer.phone.replace(/[^\d+]/g, "");

  const contactActions = [
    { label: "Call", icon: <Call />, href: phoneDigits && `tel:${phoneDigits}` },
    { label: "Text", icon: <Sms />, href: phoneDigits && "#texts" },
    {
      label: "Email",
      icon: <Email />,
      href: customer.email && `mailto:${customer.email}`,
    },
    {
      label: "Map",
      icon: <Map />,
      href:
        customer.addressLine1 &&
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          address
        )}`,
    },
  ];

  const newest = [...customerActivity].reverse();
  const photos = newest.filter((a): a is ActivityPhoto => a.type === "photo");
  const otherActivity = newest.filter((a) => a.type !== "photo");

  return (
    <div className={pageStyle.customerDetailContainer}>
      <header className={pageStyle.header}>
        <Avatar className={pageStyle.avatar} sx={{ bgcolor: "primary.main" }}>
          {initials || "?"}
        </Avatar>
        <div className={pageStyle.headerText}>
          <Typography variant="h5">{name || "New customer"}</Typography>
          <Typography color="text.secondary">
            {id ? address || "No address yet" : "Start typing below; it saves automatically."}
          </Typography>
        </div>
        {id && (
          <div className={pageStyle.contactActions}>
            {contactActions.map((a) => (
              <Tooltip key={a.label} title={a.label}>
                <span>
                  <IconButton
                    component="a"
                    href={a.href || undefined}
                    target={a.label === "Map" ? "_blank" : undefined}
                    disabled={!a.href}
                    aria-label={a.label}
                    className={pageStyle.contactButton}
                  >
                    {a.icon}
                  </IconButton>
                </span>
              </Tooltip>
            ))}
          </div>
        )}
      </header>

      {id && (
        <div className={pageStyle.quickActions}>
          <Button
            variant="contained"
            startIcon={<Assignment />}
            component={Link}
            to={`/proposals?cid=${id}`}
          >
            New Proposal
          </Button>
          <Button
            variant="outlined"
            startIcon={<AccessTime />}
            component={Link}
            to={`/appointments?cid=${id}`}
          >
            Schedule
          </Button>
          <Button
            variant="outlined"
            startIcon={<MUIPhoto />}
            component={Link}
            to={`/photos/details?cid=${id}`}
          >
            Add Photo
          </Button>
          <Button
            variant="outlined"
            startIcon={<Email />}
            component={Link}
            to={`/emails?new=1&refType=customer&refId=${id}`}
          >
            Email
          </Button>
        </div>
      )}

      <div className={pageStyle.columns}>
        <Section title="Contact info">
          <form
            className={pageStyle.contactForm}
            noValidate
            autoComplete="off"
          >
            <FormControl>
              <InputLabel htmlFor="firstNameInput">First Name</InputLabel>
              <OutlinedInput
                value={customer.firstName}
                onChange={(e) => updateCustomer({ firstName: e.target.value })}
                id="firstNameInput"
                label="First Name"
              />
            </FormControl>
            <FormControl>
              <InputLabel htmlFor="lastNameInput">Last Name</InputLabel>
              <OutlinedInput
                value={customer.lastName}
                onChange={(e) => updateCustomer({ lastName: e.target.value })}
                id="lastNameInput"
                label="Last Name"
              />
            </FormControl>
            <FormControl className={pageStyle.wide}>
              <MuiTelInput
                defaultCountry="US"
                value={customer.phone}
                onChange={(e) => updateCustomer({ phone: e })}
                id="PhoneNumberInput"
                label="Phone Number"
              />
            </FormControl>
            <FormControl className={pageStyle.narrow}>
              <InputLabel htmlFor="phoneExtInput">Ext.</InputLabel>
              <OutlinedInput
                value={customer.phoneExt}
                onChange={(e) => updateCustomer({ phoneExt: e.target.value })}
                id="phoneExtInput"
                label="Ext."
              />
            </FormControl>
            <FormControl className={pageStyle.full}>
              <InputLabel htmlFor="emailInput">Email</InputLabel>
              <OutlinedInput
                value={customer.email}
                onChange={(e) => updateCustomer({ email: e.target.value })}
                id="emailInput"
                label="Email"
                type="email"
              />
            </FormControl>
            <FormControl className={pageStyle.full}>
              <InputLabel htmlFor="addressLine1Input">Address</InputLabel>
              <OutlinedInput
                value={customer.addressLine1}
                onChange={(e) =>
                  updateCustomer({ addressLine1: e.target.value })
                }
                id="addressLine1Input"
                label="Address"
              />
            </FormControl>
            <FormControl className={pageStyle.full}>
              <InputLabel htmlFor="addressLine2Input">
                Apt, suite, etc.
              </InputLabel>
              <OutlinedInput
                value={customer.addressLine2}
                onChange={(e) =>
                  updateCustomer({ addressLine2: e.target.value })
                }
                id="addressLine2Input"
                label="Apt, suite, etc."
              />
            </FormControl>
            <FormControl className={pageStyle.full}>
              <InputLabel htmlFor="cityInput">City</InputLabel>
              <OutlinedInput
                value={customer.city}
                onChange={(e) => updateCustomer({ city: e.target.value })}
                id="cityInput"
                label="City"
              />
            </FormControl>
            <FormControl className={pageStyle.narrow}>
              <InputLabel htmlFor="stateInput">State</InputLabel>
              <Select
                id="stateInput"
                label="State"
                value={customer.state}
                onChange={(e) => updateCustomer({ state: e.target.value })}
              >
                {states.map(({ name, abbreviation }) => (
                  <MenuItem key={abbreviation} value={abbreviation}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={pageStyle.wide}>
              <InputLabel htmlFor="zipInput">Zip Code</InputLabel>
              <OutlinedInput
                value={customer.zip}
                onChange={(e) => updateCustomer({ zip: e.target.value })}
                id="zipInput"
                label="Zip Code"
                inputMode="numeric"
              />
            </FormControl>
            <FormControl className={pageStyle.full}>
              <InputLabel htmlFor="notesInput">Notes</InputLabel>
              <OutlinedInput
                value={customer.notes}
                onChange={(e) => updateCustomer({ notes: e.target.value })}
                multiline
                minRows={3}
                id="notesInput"
                label="Notes"
              />
            </FormControl>
          </form>
          {id && (
            <Button
              color="error"
              startIcon={<Delete />}
              onClick={handleDelete}
              className={pageStyle.deleteButton}
            >
              Delete customer
            </Button>
          )}
        </Section>

        {id && (
          <div className={pageStyle.sideColumn}>
            <div id="texts">
              <Section title="Text messages">
                <SmsThread customer={customer} />
              </Section>
            </div>

            <Section title="Activity">
              {otherActivity.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No proposals or appointments yet.
                </Typography>
              ) : (
                <List disablePadding>
                  {otherActivity.map((activity) => {
                    switch (activity.type) {
                      case "proposal":
                        return (
                          <ListItemButton
                            key={activity.id}
                            component={Link}
                            to={`/proposals?id=${activity.id}`}
                            className={pageStyle.activityRow}
                          >
                            <ListItemIcon>
                              <Assignment color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Proposal · ${formatMoney(
                                activity.totalAmount
                              )}`}
                              secondary={new Date(
                                activity.createdAt
                              ).toLocaleDateString()}
                            />
                            {activity.archived && (
                              <Chip
                                size="small"
                                label="Archived"
                                sx={{ mr: 0.5 }}
                              />
                            )}
                            <Chip
                              size="small"
                              label={activity.status}
                              color={statusToColor(
                                activity.status as APIProposalStatus
                              )}
                            />
                          </ListItemButton>
                        );
                      case "appointment":
                        return (
                          <ListItemButton
                            component={Link}
                            to={`/appointments?id=${activity.id}`}
                            key={activity.id}
                            className={pageStyle.activityRow}
                          >
                            <ListItemIcon>
                              <AccessTime color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Sales call"
                              secondary={new Date(
                                activity.startTime
                              ).toLocaleString([], {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })}
                            />
                          </ListItemButton>
                        );
                      default:
                        return null;
                    }
                  })}
                </List>
              )}
            </Section>

            <Section title="Communication">
              {emails.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No emails yet.
                </Typography>
              ) : (
                <List disablePadding>
                  {emails.map((e) => (
                    <ListItemButton
                      key={e.id}
                      component={Link}
                      to={`/emails?id=${e.id}`}
                      className={pageStyle.activityRow}
                    >
                      <ListItemIcon>
                        <MailOutline color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={e.subject || "(no subject)"}
                        secondary={
                          e.generatedAt
                            ? new Date(e.generatedAt).toLocaleDateString()
                            : ""
                        }
                        primaryTypographyProps={{ noWrap: true }}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        color={e.sentAt ? "success" : "warning"}
                        label={e.sentAt ? "Sent" : "Pending"}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Section>

            <Section title={`Photos${photos.length ? ` (${photos.length})` : ""}`}>
              {photos.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No photos yet.
                </Typography>
              ) : (
                <div className={pageStyle.photoGrid}>
                  {photos.map((p) => (
                    <Link
                      key={p.id}
                      to={`/photos/details?id=${p.id}&cid=${id}`}
                      className={pageStyle.photoThumb}
                    >
                      <img src={p.photoData} alt="Customer photo" />
                    </Link>
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerDetailsPage;
